import { BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import useStakingUtils from 'src/components/Staking/hooks/useStakingUtils';
import { COMMON_UI_UTILS } from '@drift/common';
import { useRequestInsuranceFundUnstake } from 'src/hooks/driftActions/insuranceFund/useRequestInsuranceFundUnstake';
import { useCallback, useEffect, useState } from 'react';
import useModal from 'src/components/Modals/hooks/useModal';
import dayjs from 'dayjs';
import { useTriggerRefreshIfStakeData } from 'src/hooks/query/ifData/useAllIfData';
import { STANDARD_DATE_FORMAT } from 'src/constants/dates';

interface Params {
	defaultSelectedMarket: SpotMarketConfig;
	isDriftStaking: boolean | undefined;
	onConfirm?: () => void;
}

export default function useUnstakeForm({
	defaultSelectedMarket,
	isDriftStaking,
	onConfirm,
}: Params) {
	const { show } = useModal();

	const [isUsingMaxAmount, setIsUsingMaxAmount] = useState(false);

	const {
		amount,
		setAmount,
		amountBignum,
		selectedMarket,
		selectedMarketStakeData,
		selectedMarketId,
		excludedMarkets,
		unstakingPeriod,
		unstakingPeriodAsDays,
		canWithdraw,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		displayPrecision,
		isWalletConnected,
		userStakeLoaded,
		userNeedsStakeAccount,
		hasExistingUnstakeRequest,
		unstakeUtilizationCutoffResult,
		handleSelectedMarketChange,
		handleCancelUnstakeRequest,
		handleWithdrawUnstakedAssets,
		isSubmitting,
		setIsSubmitting,
		captureEvent,
	} = useStakingUtils({ defaultSelectedMarket, isDriftStaking, onConfirm });

	const { requestInsuranceFundUnstake } = useRequestInsuranceFundUnstake();

	// Check for exceeded max amount.
	const currentStakeBigNum =
		selectedMarketStakeData?.userStake?.currentStakeBigNum;
	const exceededMax = amountBignum.gt(
		currentStakeBigNum ?? BigNum.zero(selectedMarket.precisionExp)
	);
	const maxAmountString = currentStakeBigNum?.print();

	useEffect(() => {
		if (exceededMax && isUsingMaxAmount && maxAmountString) {
			setAmount(maxAmountString);
		}
	}, [exceededMax, isUsingMaxAmount, maxAmountString]);

	const withdrawPaused =
		selectedMarketStakeData?.pausedOperations.includes(
			COMMON_UI_UTILS.InsuranceFundOperationsMap.REMOVE
		) ?? true;

	// Able to withdraw new unstake request after 2 weeks:
	const newWithdrawalDate = new Date(Date.now() + unstakingPeriod);

	const handleAmountChange = useCallback((newValue: string) => {
		setIsUsingMaxAmount(false);
		setAmount(newValue);
	}, []);

	const handleMaxAmountClick = useCallback(() => {
		if (maxAmountString) {
			setAmount(maxAmountString);
			setIsUsingMaxAmount(true);
		}
	}, [maxAmountString]);
	const refreshIfStakeData = useTriggerRefreshIfStakeData();

	const handleSubmit = useCallback(async () => {
		if (!isWalletConnected) {
			show({ type: 'connect_wallet', props: {} });
			return;
		}

		setIsSubmitting(true);

		const result = await requestInsuranceFundUnstake({
			displayAmount: amount,
			withdrawalDate: dayjs(newWithdrawalDate).format(STANDARD_DATE_FORMAT),
			amount: amountBignum.val,
			marketIndex: selectedMarket.marketIndex,
		});

		setIsSubmitting(false);

		setTimeout(() => refreshIfStakeData(), 1000);

		captureEvent('submitted_unstake_request', {
			spot_market_symbol: selectedMarket.symbol,
		});

		if (result) {
			onConfirm?.();
		}
	}, [
		requestInsuranceFundUnstake,
		captureEvent,
		onConfirm,
		selectedMarket,
		amount,
		amountBignum,
		newWithdrawalDate,
		isWalletConnected,
		show,
	]);

	const submitButtonContent = isWalletConnected ? 'Unstake' : 'Connect Wallet';

	const requestWithdrawPaused =
		selectedMarketStakeData?.pausedOperations.includes(
			COMMON_UI_UTILS.InsuranceFundOperationsMap.REQUEST_REMOVE
		) ?? true;

	const allowUnstake =
		userStakeLoaded &&
		!requestWithdrawPaused &&
		!isSubmitting &&
		!userNeedsStakeAccount &&
		!hasExistingUnstakeRequest &&
		!exceededMax &&
		!!amount &&
		amountBignum.gtZero();

	const isSubmitDisabled = isWalletConnected && !allowUnstake;

	return {
		amount,
		setAmount,
		amountBignum,
		selectedMarket,
		selectedMarketStakeData,
		selectedMarketId,
		excludedMarkets,
		unstakingPeriodAsDays,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		canWithdraw,
		withdrawPaused,
		newWithdrawalDate,
		requestWithdrawPaused,
		unstakeUtilizationCutoffResult,
		hasExistingUnstakeRequest,
		displayPrecision,
		isWalletConnected,
		isSubmitDisabled,
		isSubmitting,
		submitButtonContent,
		handleAmountChange,
		handleMaxAmountClick,
		handleSelectedMarketChange,
		handleCancelUnstakeRequest,
		handleWithdrawUnstakedAssets,
		handleSubmit,
	};
}
